import React from 'react';
import { Heading, List, ListItem, Stack, Text, TextLink } from '@origin-digital/ods-core';
import { CustomerType } from '../../../types';

export interface IEstimatesDialog {
  onlineCreditAmount?: number;
  customerType?: CustomerType;
}

const getLinks = (customerType: CustomerType) => {
  if (customerType === 'residential') {
    return {
      energyFactSheetUrl: 'https://www.originenergy.com.au/pricing.html',
      additionalChargesUrl: 'https://www.originenergy.com.au/pricing/additional-charges/',
    };
  }

  return {
    energyFactSheetUrl: 'https://www.originenergy.com.au/business/pricing.html',
    additionalChargesUrl:
      'https://www.originenergy.com.au/business/electricity-and-gas/pricing/additional-charges/',
  };
};

export const estimatesDialog = ({ customerType = 'residential' }: IEstimatesDialog) => {
  const { energyFactSheetUrl, additionalChargesUrl } = getLinks(customerType);
  return (
    <Stack data-id="annual-estimate-dialog" space="large">
      <Heading variant="h3">Annual estimates and percentage comparisons</Heading>
      <Stack space="xsmall">
        <Text weight="medium">About annual estimates for electricity</Text>
        <Text>
          All amounts shown are estimates based on usage for an average or typical customer. Actual
          charges will vary based on your individual usage, meter type and distributor.
        </Text>
        <Text>How we calculate estimates depends on what state you're in.</Text>
      </Stack>
      <Stack space="xsmall">
        <Text weight="medium">Victoria</Text>
        <Text>
          The usage estimates are based on electricity usage benchmarks set by the Essential
          Services Commission (ESC). This is called the Victorian Default Offer (VDO).
        </Text>
      </Stack>
      <Stack space="xsmall">
        <Text weight="medium">NSW, QLD, SA</Text>
        <Text>
          We have calculated the estimated annual cost using benchmark electricity usage for your
          network and tariff type, consistent with the approach used for AER Default Market Offer
          (DMO) comparison prices. The AER sets the DMO, a price cap and comparison price that
          retailers use to support consistent comparison of electricity plans.
        </Text>
      </Stack>
      <Stack space="xsmall">
        <Text weight="medium">ACT</Text>
        <Text>
          In ACT, the reference price is set by the Treasurer and the Minister for Water, Energy and
          Emissions Reduction.
        </Text>
        <Text>We do not sell electricity in WA.</Text>
      </Stack>
      <Stack space="xsmall">
        <Text weight="medium">Comparison percentages for electricity</Text>
        <Text>
          All energy companies compare their prices for electricity against the relevant reference
          price (in NSW, QLD and SA this is known as the comparison price set by the AER). This
          percentage figure is there to help you compare electricity plans by showing whether the
          plan prices are more or less than the reference price/comparison price.
        </Text>
      </Stack>
      <Stack space="xsmall">
        <Text weight="medium">About annual estimates for natural gas</Text>
        <Text>
          In all states, natural gas benchmarks are based on our mass market gas portfolio.
        </Text>
      </Stack>
      <Stack space="small">
        <Text weight="medium">
          What’s included - comparison percentage and estimated yearly costs
        </Text>
        <Stack space="xsmall">
          <Text>Included:</Text>
          <List>
            <ListItem>GST (unless otherwise stated)</ListItem>
          </List>
        </Stack>
        <Stack space="xsmall">
          <Text>Not included:</Text>
          <List>
            <ListItem>Optional product add-on charges (eg for GreenPower)</ListItem>
            <ListItem>Any rebates which may apply to you</ListItem>
            <ListItem>Solar feed-in tariff credits that your solar system could generate</ListItem>
            <ListItem>Any conditional one-off credits</ListItem>
          </List>
        </Stack>
      </Stack>
      <Stack space="small">
        <Text weight="medium">More information</Text>
        <Text>
          For more information, see our{' '}
          <TextLink href={energyFactSheetUrl}>
            Energy fact sheets or Basic plan information documents
          </TextLink>
          . <TextLink href={additionalChargesUrl}>Additional charges</TextLink> may also apply.
        </Text>
        <Text>
          You may also be eligible for a state-government rebate on your bill. See{' '}
          <TextLink href="https://www.originenergy.com.au/pricing/concessions-rebates/">
            Concessions and rebates
          </TextLink>{' '}
          for details.
        </Text>
      </Stack>
    </Stack>
  );
};
